import { Button, CheckboxGroup, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { Settings2Icon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '@/entities/settings';

/** Опция таба для настройки видимости (ключ + отображаемое имя). */
export type DialogDataTabOption = {
  key: string;
  title: string;
};

type Props = {
  /** Все табы, доступные в текущей секции (включая динамические экспертные отчёты). */
  options: DialogDataTabOption[];
};

/**
 * Иконка-кнопка с Popover'ом для управления видимостью табов секции данных диалога.
 *
 * Состояние видимости персистируется в `useSettingsStore` как `dialogsPage.hiddenDataTabs`
 * (список скрытых ключей) — отсутствие ключа в списке = таб видим.
 */
export const DialogDataTabsSettings: FC<Props> = ({ options }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { dialogsPage, setDialogsSettings } = useSettings();
  const hiddenDataTabs = useMemo(() => dialogsPage?.hiddenDataTabs ?? [], [dialogsPage]);

  // В CheckboxGroup храним инвертированно — список ВИДИМЫХ ключей,
  // чтобы UX был «отметь, что показывать»
  const visibleKeys = useMemo(
    () => options.map((opt) => opt.key).filter((key) => !hiddenDataTabs.includes(key)),
    [options, hiddenDataTabs],
  );

  const checkboxOptions = useMemo(
    () => options.map((opt) => ({ value: opt.key, label: opt.title })),
    [options],
  );

  const handleChange = useCallback(
    (nextVisible: string[]) => {
      const nextHidden = options
        .map((opt) => opt.key)
        .filter((key) => !nextVisible.includes(key));
      setDialogsSettings({ hiddenDataTabs: nextHidden });
    },
    [options, setDialogsSettings],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          icon={<Settings2Icon className="size-4" />}
          aria-label={t('dialogs:dataTabsSettings')}
        />
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <p className="mb-3 text-sm font-medium">{t('dialogs:dataTabsSettings')}</p>
        <CheckboxGroup
          name="dialog-data-tabs-visibility"
          options={checkboxOptions}
          value={visibleKeys}
          onChange={handleChange}
          className="flex flex-col gap-2"
        />
      </PopoverContent>
    </Popover>
  );
};
